/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonActions, FormActionPipelines, FormFlowState } from '@gh/builder-shared';
import {
  AppFormGroup,
  FormState,
  FormStateBuilder,
  IS_REGION_AU_OR_NZ,
  NumberTypes,
  whenAuOrNzRegion,
} from '@gh/common-shared';
import { getControlValue, setControlValue } from '@gh/core-ui';
import { isUndefined } from 'lodash';
import { PriceBookDetailsBusinessService } from '../../../resources/price-book-details-business.service';
import { PbHousePlan } from '../../../resources/price-book.model';

@Component({
  selector: 'gh-pb-details-hp-form',
  template: require('./pb-details-hp-form.component.html'),
  styles: [require('./pb-details-hp-form.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PbDetailsHpFormComponent {
  @Input() editable: boolean = true;

  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() start = new EventEmitter();
  @Output() apply = new EventEmitter<PbHousePlan>();
  @Output() cancel = new EventEmitter();

  inEditing = false;
  recalculationNeeded = false;

  form: FormState<PbHousePlan>;

  private _housePlan: PbHousePlan;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private formStateBuilder: FormStateBuilder,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private business: PriceBookDetailsBusinessService) {

  }

  get calcPriceLabel(): string {
    return IS_REGION_AU_OR_NZ ? 'lbl_calculated_price_ex_gst' : 'lbl_calculated_price';
  }

  get salesPriceLabel(): string {
    return IS_REGION_AU_OR_NZ ? 'lbl_sale_price_incl_gst' : 'lbl_sale_price';
  }

  get costMeterLabel(): string {
    return IS_REGION_AU_OR_NZ ? 'lbl_cost_meter' : 'lbl_cost_ft';
  }

  get salesPriceMeterLabel(): string {
    return IS_REGION_AU_OR_NZ ? 'lbl_price_meter' : 'lbl_price_ft';
  }

  @Input()
  set housePlan(housePlan: PbHousePlan) {
    this._housePlan = housePlan;
    this.recalculationNeeded = false;

    this.form = this.formStateBuilder.buildPlain<PbHousePlan>({
      value: housePlan,
      enabled: false,
      submitValue: 'all',
      children: {
        housePlanId: void 0,
        isManual: void 0,
        cost: void 0,
        createdDate: void 0,
        dirtyType: void 0,
        code: void 0,
        floorArea: void 0,
        isQq: void 0,
        isQqAllowed: { visible: !housePlan.isQq },
        qqMarkupPct: { required: true, type: NumberTypes.Percents_2 },
        overheadMarkupPct: { visible: !housePlan.isQq, required: true, type: NumberTypes.Percents_2 },
        contingencyMarkupPct: { visible: !housePlan.isQq, required: true, type: NumberTypes.Percents_2 },
        profitMarkupPct: { visible: !housePlan.isQq, required: true, type: NumberTypes.Percents_2 },
        totalPct: { visible: !housePlan.isQq, enabled: false, type: NumberTypes.Percents_2 },
        priceRoundUp: { required: true, type: NumberTypes.RoundUp },
        calcPrice: { enabled: false, type: NumberTypes.SignedPrice },
        calcPriceWithGst: { visible: whenAuOrNzRegion(), enabled: false, type: NumberTypes.SignedPrice },
        salesPrice: { required: true, type: NumberTypes.Price },
        profit: { enabled: false, type: NumberTypes.Price },
        costMeter: { enabled: false, type: NumberTypes.SignedPrice },
        salesPriceMeter: { enabled: false, type: NumberTypes.SignedPrice },
        trueProfit: { enabled: false, type: NumberTypes.SignedPrice },
      },
    });
  }

  get housePlan(): PbHousePlan {
    return this._housePlan;
  }

  onStartEdit(): void {
    this.start.emit();
    this.setEnabled(true);
    this.changeDetectorRef.markForCheck();
  }

  onCancel(): void {
    this.setEnabled(false);
    this.housePlan = this._housePlan;

    this.cancel.emit();
    this.changeDetectorRef.markForCheck();
  }

  onApply(): void {
    const applyFlow = this.pipelines.recalculate({
      execute: this.commonActions.execute((state: FormFlowState<PbHousePlan>) =>
        this.business.calculateHousePlan(state.value)),
      finish: (state: FormFlowState<PbHousePlan>) => {
        this.apply.emit(state.value);
        this.setEnabled(false);
        this.changeDetectorRef.markForCheck();
      },
    });

    applyFlow.run({ form: this.form, value: this.form.submitValue });
  }

  onRecalculate(): void {
    const recalculateFlow = this.pipelines.recalculate({
      execute: this.commonActions.execute((state: FormFlowState<PbHousePlan>) =>
        this.business.calculateHousePlan(state.value)),
      finish: (state: FormFlowState<PbHousePlan>) => {
        this.form.group.patchValue(state.value);
        this.recalculationNeeded = false;
        this.changeDetectorRef.markForCheck();
      },
    });

    recalculateFlow.run({ form: this.form, value: this.form.submitValue });
  }

  onSalesPriceChange(): void {
    setControlValue(this.form.group, 'isManual', true);
    this.recalculationNeeded = true;
    this.changeDetectorRef.markForCheck();
  }

  onSalesPriceBlur(): void {
    const value = getControlValue(this.form.group, 'salesPrice');

    if (isUndefined(value)) {
      setControlValue(this.form.group, 'salesPrice', 0);
      this.changeDetectorRef.markForCheck();
    }
  }

  onMasterFieldChange(field: keyof PbHousePlan): void {
    const { isQq } = this.housePlan;

    if (isQq) {
      switch (field) {
        case 'qqMarkupPct':
        case 'priceRoundUp':
          this.recalculationNeeded = true;
          setControlValue(this.form.group, 'isManual', false);
          this.changeDetectorRef.markForCheck();
          break;
        default:
          break;
      }
    } else {
      switch (field) {
        case 'overheadMarkupPct':
        case 'contingencyMarkupPct':
        case 'profitMarkupPct':
        case 'priceRoundUp':
          this.recalculationNeeded = true;
          setControlValue(this.form.group, 'isManual', false);
          this.changeDetectorRef.markForCheck();
          break;
        default:
          break;
      }
    }
  }

  private setEnabled(enabled: boolean): void {
    this.inEditing = enabled;

    const group = <AppFormGroup>this.form.group;
    group.updateSchema({ enabled });
  }
}
